/**
 * Minimal pub/sub store powering the app-wide toast API.
 * Call `toast.success(...)` / `toast.error(...)` from anywhere; mount
 * `<ToastContainer />` once to render active toasts.
 */

export type ToastVariant = "success" | "error";

export interface ToastItem {
  id: string;
  message: string;
  variant: ToastVariant;
}

type Listener = (toasts: ToastItem[]) => void;

const DISPLAY_DURATION = 3000;

let toasts: ToastItem[] = [];
const listeners = new Set<Listener>();

const emit = () => listeners.forEach((listener) => listener(toasts));

export const subscribeToasts = (listener: Listener): (() => void) => {
  listeners.add(listener);
  listener(toasts);
  return () => listeners.delete(listener);
};

export const dismissToast = (id: string): void => {
  toasts = toasts.filter((item) => item.id !== id);
  emit();
};

const pushToast = (message: string, variant: ToastVariant): void => {
  const id = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  toasts = [...toasts, { id, message, variant }];
  emit();
  setTimeout(() => dismissToast(id), DISPLAY_DURATION);
};

export const toast = {
  success: (message: string) => pushToast(message, "success"),
  error: (message: string) => pushToast(message, "error"),
};
